import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AiFillCheckCircle } from 'react-icons/ai';
import { FaMicrophone, FaStopCircle } from 'react-icons/fa';
import { Button } from 'reactstrap';
import { AudioInput, AudioTime } from './components/styled';
import { differenceInSeconds } from 'date-fns';
import { toast } from 'react-toastify';
import ConversationService from '../../../../../ServicePanel/service/index';
import { addMessageConversationInAttendance } from 'store/action/conversation-in-attendance-action';
import MicRecorder from 'mic-recorder-to-mp3';
import FlexChatIntegration from 'views/ServicePanel/components/QueueContent/components/Contact/service/FlexChatIntegration';

let intervalId = null;
const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const useRecorder = ({
  id,
  setRecording,
  conversation = null,
  inReplyToMessageId,
  setInReplyToMessageId,
  isDisabled = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [timeCount, setTimeCount] = useState('00:00');
  const dispatch = useDispatch();

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const getTime = (startTime) => {
    let differenceFromNow = differenceInSeconds(
      new Date(),
      new Date(startTime)
    );
    const minutes = Math.floor(differenceFromNow / 60);
    differenceFromNow -= minutes * 60;
    const seconds = Math.floor(differenceFromNow);

    const getFomattedDigit = (digit) => `${String(digit).padStart(2, '0')}`;
    return `${getFomattedDigit(minutes)}:${getFomattedDigit(seconds)}`;
  };

  const startTimer = () => {
    const nowTime = new Date();
    intervalId = setInterval(() => {
      setTimeCount(getTime(nowTime));
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(intervalId);
    intervalId = null;
    setTimeCount('00:00');
  };

  useEffect(() => {
    return () => clearInterval(intervalId);
  }, []);

  const startRecording = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.error('Permissão de microfone negada:', e);
      toast.error(
        'Permissão para usar o microfone é necessária para gravar áudio.'
      );
      return;
    }

    setIsRecording(true);
    setRecording(true);
    startTimer();

    if (conversation?.canal === 'instagram') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: 'audio/mp4'
        });
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.start();
      } catch (e) {
        console.error('Erro ao iniciar MediaRecorder para MP4:', e);
        toast.error(
          'Seu navegador pode não suportar a gravação em MP4. Tente atualizar ou usar outro navegador.'
        );
        stopRecording();
      }
    } else {
      Mp3Recorder.start().catch((e) => console.error(e));
    }
  };

  const stopRecording = () => {
    if (conversation?.canal === 'instagram') {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
      }
    } else {
      Mp3Recorder.stop();
    }

    setIsRecording(false);
    setRecording(false);
    stopTimer();
  };

  const handleSendFile = (file) => {
    const formatedData = new FormData();
    formatedData.append('idDaConversa', id);
    formatedData.append('cliente_ou_atendente', 'atendente');
    formatedData.append('escrita_por', localStorage.getItem('name'));
    formatedData.append(
      'legenda',
      `audio_atendente_${new Date().toISOString()}`
    );
    formatedData.append('response_type', 'audio');
    formatedData.append('type', 'audio');
    formatedData.append('file', file);

    const channel = conversation?.canal;

    if (channel === 'whatsapp') {
      if (inReplyToMessageId) {
        formatedData.append('inReplyTo', inReplyToMessageId);
        setInReplyToMessageId(null);
      }
      FlexChatIntegration.uploadFile(id, formatedData)
        .then((res) =>
          dispatch(addMessageConversationInAttendance({ ...res.data }, id))
        )
        .catch((err) => console.log(err));
    } else if (channel === 'chat') {
      FlexChatIntegration.uploadFileWebchat(formatedData)
        .then((res) =>
          dispatch(addMessageConversationInAttendance({ ...res.data.data }, id))
        )
        .catch((err) => console.log(err));
    } else if (channel === 'facebook') {
      FlexChatIntegration.uploadFileFacebook(formatedData, id)
        .then((res) =>
          dispatch(addMessageConversationInAttendance({ ...res.data.data }, id))
        )
        .catch((err) => console.log(err));
    } else if (channel === 'instagram') {
      FlexChatIntegration.uploadFileInstagram(formatedData, id)
        .then((res) =>
          dispatch(addMessageConversationInAttendance({ ...res.data.data }, id))
        )
        .catch((err) => console.log(err));
    } else if (channel === 'telegram') {
      FlexChatIntegration.uploadFileTelegram(formatedData, id)
        .then((res) =>
          dispatch(addMessageConversationInAttendance({ ...res.data.data }, id))
        )
        .catch((err) => console.log(err));
    } else {
      ConversationService.uploadFile(formatedData)
        .then((res) =>
          dispatch(addMessageConversationInAttendance({ ...res.data }, id))
        )
        .catch((err) => console.log(err));
    }
  };

  const sendRecording = () => {
    try {
      if (conversation?.canal === 'instagram') {
        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: 'audio/mp4'
          });
          const audioFile = new File([audioBlob], `audio_${Date.now()}.mp4`, {
            type: 'audio/mp4'
          });
          handleSendFile(audioFile);
          audioChunksRef.current = [];
        };
        stopRecording();
      } else {
        Mp3Recorder.stop()
          .getMp3()
          .then(([buffer, blob]) => {
            const audioFile = new File([blob], `audio_${Date.now()}.mp3`, {
              type: 'audio/mpeg'
            });
            handleSendFile(audioFile);
          })
          .catch((e) => console.log(e));

        stopRecording();
      }
    } catch (err) {
      toast.error(err.message || 'Ops, ocorreu um erro ao enviar a gravação!', {
        autoClose: 3000,
        closeOnClick: true
      });
      stopRecording();
    }
  };

  return (
    <>
      {!isRecording ? (
        <Button
          className="shadow-none"
          color="link"
          size="sm"
          title="Enviar áudio"
          onClick={startRecording}
          disabled={isRecording || isDisabled}
        >
          <FaMicrophone size={20} />
        </Button>
      ) : (
        <AudioInput>
          <Button
            className="shadow-none controlBtn"
            color="link"
            size="sm"
            title="Enviar"
            onClick={sendRecording}
            disabled={!isRecording}
          >
            <AiFillCheckCircle color="#5cb85c" />
          </Button>
          <div className="blinker" />
          <AudioTime>{timeCount}</AudioTime>
          <Button
            className="shadow-none controlBtn"
            color="link"
            size="sm"
            title="Cancelar"
            onClick={stopRecording}
            disabled={!isRecording}
          >
            <FaStopCircle color="#d9534f" />
          </Button>
        </AudioInput>
      )}
    </>
  );
};

export default useRecorder;
